import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Input } from "@/components/ui/input";
import { NumberInput } from "@/components/ui/number-input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { ImageIcon, Upload, MapPin, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import type { Tables } from "@/integrations/supabase/types";
import { toast } from "sonner";

// PR-AY2a (architectural spike): replace `Record<string, unknown>` with the
// auto-generated Supabase Tables<> helper. The DB schema is the source of
// truth for nullability — `organizations.address: string | null`,
// `halls.latitude: number | null`, etc. — so when AY3 enables
// strictNullChecks the type checker has accurate info for free.
//
// `org.settings` is a JSONB column typed as `Json` in the generated types;
// shape varies per organization. Component uses `(org.settings as any)?.x`
// at the few JSONB access sites — acceptable since the JSONB shape isn't
// validated at the DB level.
type OrgRow = Tables<"organizations">;
type HallRow = Tables<"halls">;

async function generateColorVariant(imageUrl: string, color: "black" | "white"): Promise<Blob> {
  const img = new Image();
  img.crossOrigin = "anonymous";
  await new Promise<void>((resolve, reject) => {
    img.onload = () => resolve();
    img.onerror = reject;
    img.src = imageUrl;
  });
  const canvas = document.createElement("canvas");
  canvas.width = img.width;
  canvas.height = img.height;
  const ctx = canvas.getContext("2d")!;
  ctx.drawImage(img, 0, 0);
  ctx.globalCompositeOperation = "source-in";
  ctx.fillStyle = color;
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  return new Promise((resolve) => canvas.toBlob((b) => resolve(b!), "image/png"));
}

function useDayLabels() {
  const { t } = useTranslation("admin");
  return [
    t("settings.clubVenue.dayMon"),
    t("settings.clubVenue.dayTue"),
    t("settings.clubVenue.dayWed"),
    t("settings.clubVenue.dayThu"),
    t("settings.clubVenue.dayFri"),
    t("settings.clubVenue.daySat"),
    t("settings.clubVenue.daySun"),
  ];
}

function StaffAccessCodes({ orgId }: { orgId: string }) {
  const { t } = useTranslation("admin");
  const DAY_LABELS = useDayLabels();
  const [codes, setCodes] = useState<any[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [scheduleType, setScheduleType] = useState<"always" | "scheduled">("always");
  const [days, setDays] = useState<number[]>([1, 2, 3, 4, 5]);
  const [startTime, setStartTime] = useState("06:00");
  const [endTime, setEndTime] = useState("23:00");

  const fetchCodes = async () => {
    const { data } = await supabase.from("staff_access_codes" as any).select("*").eq("organization_id", orgId).order("created_at");
    setCodes(data ?? []);
  };

  useState(() => { fetchCodes(); });

  const handleSave = async () => {
    if (!name.trim() || !code.trim()) { toast.error(t("settings.clubVenue.errorNameCodeRequired")); return; }
    const schedule = scheduleType === "always" ? null : { days, start: startTime, end: endTime };
    const payload = { organization_id: orgId, name: name.trim(), code: code.trim(), schedule, is_active: true };

    if (editId) {
      await supabase.from("staff_access_codes" as any).update(payload).eq("id", editId);
    } else {
      await supabase.from("staff_access_codes" as any).insert(payload);
    }
    toast.success(editId ? t("settings.clubVenue.codeUpdated") : t("settings.clubVenue.codeCreated"));
    setShowForm(false); setEditId(null); setName(""); setCode("");
    setScheduleType("always"); setDays([1, 2, 3, 4, 5]);
    fetchCodes();
  };

  const handleDelete = async (id: string) => {
    await supabase.from("staff_access_codes" as any).delete().eq("id", id);
    toast.success(t("settings.clubVenue.codeDeleted"));
    fetchCodes();
  };

  const toggleActive = async (id: string, current: boolean) => {
    await supabase.from("staff_access_codes" as any).update({ is_active: !current }).eq("id", id);
    fetchCodes();
  };

  const startEdit = (c: any) => {
    setEditId(c.id); setName(c.name); setCode(c.code);
    setScheduleType(c.schedule ? "scheduled" : "always");
    if (c.schedule) { setDays(c.schedule.days ?? [1,2,3,4,5]); setStartTime(c.schedule.start ?? "06:00"); setEndTime(c.schedule.end ?? "23:00"); }
    setShowForm(true);
  };

  return (
    <section className="border-t pt-6">
      <h4 className="font-medium mb-1">{t("settings.clubVenue.staffCodes")}</h4>
      <p className="text-xs text-muted-foreground mb-3">{t("settings.clubVenue.staffCodesDesc")}</p>

      <div className="space-y-2 mb-3">
        {codes.map((c: any) => (
          <div key={c.id} className={`flex items-center justify-between rounded-lg border p-3 ${c.is_active ? "" : "opacity-50"}`}>
            <div>
              <p className="text-sm font-medium">{c.name}</p>
              <p className="text-xs text-muted-foreground font-mono">{c.code}</p>
              {c.schedule && (
                <p className="text-[10px] text-muted-foreground">
                  {c.schedule.days?.map((d: number) => DAY_LABELS[d - 1]).join(", ")} {c.schedule.start}–{c.schedule.end}
                </p>
              )}
              {!c.schedule && <p className="text-[10px] text-muted-foreground">{t("settings.clubVenue.alwaysValid")}</p>}
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={c.is_active} onCheckedChange={() => toggleActive(c.id, c.is_active)} />
              <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => startEdit(c)}>{t("settings.clubVenue.btnEdit")}</Button>
              <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-destructive" onClick={() => handleDelete(c.id)}>{t("settings.clubVenue.btnRemove")}</Button>
            </div>
          </div>
        ))}
        {codes.length === 0 && !showForm && <p className="text-sm text-muted-foreground">{t("settings.clubVenue.noStaffCodes")}</p>}
      </div>

      {showForm ? (
        <div className="rounded-lg border p-4 space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label className="text-xs">{t("settings.clubVenue.fieldName")}</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t("settings.clubVenue.placeholderStaffName")} className="h-8 text-sm" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">{t("settings.clubVenue.fieldCode")}</Label>
              <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="4821#" className="h-8 text-sm font-mono" />
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-xs">{t("settings.clubVenue.fieldSchedule")}</Label>
            <div className="flex gap-3">
              <label className="flex items-center gap-1.5 text-sm"><input type="radio" checked={scheduleType === "always"} onChange={() => setScheduleType("always")} /> {t("settings.clubVenue.always")}</label>
              <label className="flex items-center gap-1.5 text-sm"><input type="radio" checked={scheduleType === "scheduled"} onChange={() => setScheduleType("scheduled")} /> {t("settings.clubVenue.scheduled")}</label>
            </div>
            {scheduleType === "scheduled" && (
              <div className="space-y-2">
                <div className="flex flex-wrap gap-1">
                  {DAY_LABELS.map((d, i) => (
                    <button key={i} type="button" onClick={() => setDays(prev => prev.includes(i + 1) ? prev.filter(x => x !== i + 1) : [...prev, i + 1])}
                      className={`px-2 py-1 rounded text-xs font-medium border ${days.includes(i + 1) ? "bg-primary text-primary-foreground border-primary" : "border-border"}`}>{d}</button>
                  ))}
                </div>
                <div className="flex gap-2 items-center">
                  <Input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} className="h-8 text-sm w-28" />
                  <span className="text-sm">–</span>
                  <Input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} className="h-8 text-sm w-28" />
                </div>
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={handleSave}>{editId ? t("settings.clubVenue.btnSave") : t("settings.clubVenue.btnAdd")}</Button>
            <Button size="sm" variant="outline" onClick={() => { setShowForm(false); setEditId(null); }}>{t("settings.clubVenue.btnCancel")}</Button>
          </div>
        </div>
      ) : (
        <Button variant="outline" size="sm" onClick={() => setShowForm(true)} className="gap-1">+ {t("settings.clubVenue.btnAddCode")}</Button>
      )}
    </section>
  );
}

interface Props {
  org: OrgRow;
  updateField: (field: string, value: unknown) => void;
  /** Persist a single field to DB immediately (for uploads that shouldn't wait for Save click) */
  persistField?: (field: string, value: unknown) => Promise<void>;
  hall: HallRow | null;
  updateHall: (field: string, value: unknown) => void;
}

export default function SettingsClubAndVenue({ org, updateField, persistField, hall, updateHall }: Props) {
  const { t } = useTranslation("admin");
  const [fetchingLocation, setFetchingLocation] = useState(false);

  const handleGetPosition = () => {
    if (!navigator.geolocation) {
      toast.error(t("toast.locationNotSupported"));
      return;
    }
    setFetchingLocation(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        updateHall("latitude", parseFloat(pos.coords.latitude.toFixed(6)));
        updateHall("longitude", parseFloat(pos.coords.longitude.toFixed(6)));
        toast.success(t("toast.positionFetched"));
        setFetchingLocation(false);
      },
      (err) => {
        toast.error(t("settings.clubVenue.errorFetchPosition") + ": " + err.message);
        setFetchingLocation(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  return (
    <div className="space-y-8">
      {/* ── Identitet ── */}
      <section>
        <h3 className="text-lg font-semibold mb-1">{t("settings.clubVenue.sectionIdentity")}</h3>
        <p className="text-sm text-muted-foreground mb-4">{t("settings.clubVenue.sectionIdentityDesc")}</p>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label>{t("settings.clubVenue.fieldClubName")}</Label>
            <Input value={org.name || ""} onChange={(e) => updateField("name", e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>Slug (URL)</Label>
            <Input value={org.slug || ""} onChange={(e) => updateField("slug", e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>{t("settings.clubVenue.fieldWebsite")}</Label>
            <Input value={org.website || ""} onChange={(e) => updateField("website", e.target.value)} placeholder="https://..." />
          </div>

          {/* Logotyp & App-ikon */}
          <div className="sm:col-span-2 grid gap-6 sm:grid-cols-2">
            {/* Logotyp */}
            <div className="space-y-2">
              <Label>{t("settings.clubVenue.fieldLogo")}</Label>
              <div className="flex items-start gap-4">
                <div className="h-20 w-20 rounded-lg border bg-muted flex items-center justify-center overflow-hidden shrink-0">
                  {org.logo_url ? (
                    <img src={org.logo_url} alt="Logotyp" className="h-full w-full object-contain" />
                  ) : (
                    <ImageIcon className="h-8 w-8 text-muted-foreground" />
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  <input
                    id="logo-upload"
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    className="hidden"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      if (file.size > 2 * 1024 * 1024) { toast.error(t("toast.maxFileSize")); return; }
                      if (!["image/png", "image/jpeg", "image/webp"].includes(file.type)) { toast.error(t("toast.onlyImageFormats")); return; }
                      const ext = file.name.split(".").pop();
                      const path = `${org.id}/logo.${ext}`;
                      const { error } = await supabase.storage.from("logos").upload(path, file, { upsert: true });
                      if (error) { toast.error(t("toast.couldNotUploadLogo")); return; }
                      const { data: urlData } = supabase.storage.from("logos").getPublicUrl(path);
                      const logoUrl = urlData.publicUrl + "?v=" + Date.now();
                      updateField("logo_url", logoUrl);
                      if (persistField) await persistField("logo_url", logoUrl);
                      toast.success(t("toast.logoUploaded"));
                    }}
                  />
                  <Button type="button" variant="outline" size="sm" onClick={() => document.getElementById("logo-upload")?.click()} className="gap-2">
                    <Upload className="h-4 w-4" />
                    {org.logo_url ? t("settings.clubVenue.btnChangeLogo") : t("settings.clubVenue.btnUpload")}
                  </Button>
                  <p className="text-[11px] text-muted-foreground">{t("settings.clubVenue.logoHint")}</p>
                  {org.logo_url && (
                    <button type="button" className="text-xs text-destructive hover:underline self-start" onClick={async () => {
                      updateField("logo_url", null);
                      if (persistField) await persistField("logo_url", null);
                    }}>
                      {t("settings.clubVenue.btnRemoveLogo")}
                    </button>
                  )}
                  {org.logo_url && (
                    <div className="flex gap-1 mt-1">
                      <span className="text-[10px] text-muted-foreground">{t("settings.clubVenue.variant")}:</span>
                      {(["original", "black", "white"] as const).map((variant) => (
                        <button
                          key={variant}
                          type="button"
                          className="text-[10px] px-1.5 py-0.5 rounded border hover:bg-accent"
                          onClick={async () => {
                            if (variant === "original") return;
                            try {
                              const blob = await generateColorVariant(org.logo_url!, variant);
                              const path = `${org.id}/logo_${variant}.png`;
                              const { error } = await supabase.storage.from("logos").upload(path, blob, { upsert: true, contentType: "image/png" });
                              if (error) { toast.error(t("settings.clubVenue.errorUploadVariant")); return; }
                              const { data: urlData } = supabase.storage.from("logos").getPublicUrl(path);
                              const url = urlData.publicUrl + "?v=" + Date.now();
                              updateField("logo_url", url);
                              if (persistField) await persistField("logo_url", url);
                              toast.success(t("settings.clubVenue.logoChangedTo", { variant: variant === "black" ? t("settings.clubVenue.variantBlack") : t("settings.clubVenue.variantWhite") }));
                            } catch {
                              toast.error(t("settings.clubVenue.errorCreateVariant"));
                            }
                          }}
                        >
                          {variant === "original" ? t("settings.clubVenue.variantOriginal") : variant === "black" ? t("settings.clubVenue.variantBlackLabel") : t("settings.clubVenue.variantWhiteLabel")}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* App-ikon (favicon) */}
            <div className="space-y-2">
              <Label>{t("settings.clubVenue.fieldAppIcon")}</Label>
              <div className="flex items-start gap-4">
                <div className="h-16 w-16 rounded-lg border bg-muted flex items-center justify-center overflow-hidden shrink-0">
                  {org.app_icon_url ? (
                    <img src={org.app_icon_url} alt="App-ikon" className="h-full w-full object-cover" />
                  ) : (
                    <ImageIcon className="h-6 w-6 text-muted-foreground" />
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  <input
                    id="app-icon-upload"
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/svg+xml"
                    className="hidden"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      if (file.size > 5 * 1024 * 1024) { toast.error(t("settings.clubVenue.errorMax5MB")); return; }

                      // Auto-crop to square + scale to 512×512 via canvas
                      const processIcon = (f: File): Promise<Blob> =>
                        new Promise((resolve, reject) => {
                          const img = new Image();
                          img.onload = () => {
                            const size = Math.min(img.width, img.height);
                            const sx = (img.width - size) / 2;
                            const sy = (img.height - size) / 2;
                            const canvas = document.createElement("canvas");
                            canvas.width = 512;
                            canvas.height = 512;
                            const ctx = canvas.getContext("2d")!;
                            ctx.drawImage(img, sx, sy, size, size, 0, 0, 512, 512);
                            canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error("Canvas failed")), "image/png");
                          };
                          img.onerror = reject;
                          img.src = URL.createObjectURL(f);
                        });

                      try {
                        const processed = await processIcon(file);
                        const path = `${org.id}/icon.png`;
                        const { error } = await supabase.storage.from("icons").upload(path, processed, { upsert: true, contentType: "image/png" });
                        if (error) { toast.error(t("settings.clubVenue.errorUploadAppIcon")); return; }
                        const { data: urlData } = supabase.storage.from("icons").getPublicUrl(path);
                        const url = urlData.publicUrl + "?v=" + Date.now();
                        updateField("app_icon_url", url);
                        if (persistField) await persistField("app_icon_url", url);
                        toast.success(t("settings.clubVenue.appIconSaved"));
                      } catch {
                        toast.error(t("settings.clubVenue.errorProcessImage"));
                      }
                    }}
                  />
                  <Button type="button" variant="outline" size="sm" onClick={() => document.getElementById("app-icon-upload")?.click()} className="gap-2">
                    <Upload className="h-4 w-4" />
                    {org.app_icon_url ? t("settings.clubVenue.btnChangeIcon") : t("settings.clubVenue.btnUpload")}
                  </Button>
                  <p className="text-[11px] text-muted-foreground">{t("settings.clubVenue.appIconHint")}</p>
                  {org.app_icon_url && (
                    <button type="button" className="text-xs text-destructive hover:underline self-start" onClick={async () => {
                      updateField("app_icon_url", null);
                      if (persistField) await persistField("app_icon_url", null);
                      toast.success(t("settings.clubVenue.iconRemoved"));
                    }}>
                      {t("settings.clubVenue.btnRemoveIcon")}
                    </button>
                  )}
                  {org.app_icon_url && (
                    <div className="flex gap-1 mt-1">
                      <span className="text-[10px] text-muted-foreground">{t("settings.clubVenue.variant")}:</span>
                      {(["original", "black", "white"] as const).map((variant) => (
                        <button
                          key={variant}
                          type="button"
                          className="text-[10px] px-1.5 py-0.5 rounded border hover:bg-accent"
                          onClick={async () => {
                            if (variant === "original") return;
                            try {
                              const blob = await generateColorVariant(org.app_icon_url as string, variant);
                              const path = `${(org as any).id}/icon_${variant}.png`;
                              const { error } = await supabase.storage.from("icons").upload(path, blob, { upsert: true, contentType: "image/png" });
                              if (error) { toast.error(t("settings.clubVenue.errorUploadVariant")); return; }
                              const { data: urlData } = supabase.storage.from("icons").getPublicUrl(path);
                              const url = urlData.publicUrl + "?v=" + Date.now();
                              updateField("app_icon_url", url);
                              if (persistField) await persistField("app_icon_url", url);
                              toast.success(t("settings.clubVenue.iconChangedTo", { variant: variant === "black" ? t("settings.clubVenue.variantBlack") : t("settings.clubVenue.variantWhite") }));
                            } catch {
                              toast.error(t("settings.clubVenue.errorCreateVariant"));
                            }
                          }}
                        >
                          {variant === "original" ? t("settings.clubVenue.variantOriginal") : variant === "black" ? t("settings.clubVenue.variantBlackLabel") : t("settings.clubVenue.variantWhiteLabel")}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar display toggle */}
        {org.logo_url && (
          <div className="flex items-center justify-between rounded-lg border p-4 sm:col-span-2">
            <div className="space-y-0.5">
              <Label className="text-sm font-medium">{t("settings.clubVenue.showLogoInSidebar")}</Label>
              <p className="text-xs text-muted-foreground">
                {t("settings.clubVenue.showLogoInSidebarDesc")}
              </p>
            </div>
            <Switch
              checked={((org.settings as any)?.sidebar_display ?? "logo") === "logo"}
              onCheckedChange={(checked) => {
                const currentSettings = (org.settings as any) ?? {};
                updateField("settings", {
                  ...currentSettings,
                  sidebar_display: checked ? "logo" : "name",
                });
              }}
            />
          </div>
        )}
      </section>

      {/* ── Kontakt ── */}
      <section className="border-t pt-6">
        <h4 className="font-medium mb-3">{t("settings.clubVenue.sectionContact")}</h4>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label>{t("settings.clubVenue.fieldEmail")}</Label>
            <Input type="email" value={org.contact_email || ""} onChange={(e) => updateField("contact_email", e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>{t("settings.clubVenue.fieldPhone")}</Label>
            <Input value={org.contact_phone || ""} onChange={(e) => updateField("contact_phone", e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>{t("settings.clubVenue.fieldAddress")}</Label>
            <Input value={org.address || ""} onChange={(e) => updateField("address", e.target.value)} />
          </div>
        </div>
      </section>

      {/* ── Utseende ── */}
      <section className="border-t pt-6">
        <h4 className="font-medium mb-3">{t("settings.clubVenue.sectionAppearance")}</h4>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label>{t("settings.clubVenue.fieldPrimaryColor")}</Label>
            <div className="flex gap-2">
              <input type="color" value={org.primary_color || "#1E40AF"} onChange={(e) => updateField("primary_color", e.target.value)} className="h-10 w-10 rounded-md border cursor-pointer shrink-0 p-0.5" />
              <Input value={org.primary_color || ""} onChange={(e) => updateField("primary_color", e.target.value)} placeholder="#1E40AF" />
            </div>
          </div>
          <div className="space-y-2">
            <Label>{t("settings.clubVenue.fieldSecondaryColor")}</Label>
            <div className="flex gap-2">
              <input type="color" value={org.secondary_color || "#3B82F6"} onChange={(e) => updateField("secondary_color", e.target.value)} className="h-10 w-10 rounded-md border cursor-pointer shrink-0 p-0.5" />
              <Input value={org.secondary_color || ""} onChange={(e) => updateField("secondary_color", e.target.value)} placeholder="#3B82F6" />
            </div>
          </div>
        </div>
      </section>

      {/* ── Hall sections ── */}
      {hall && (
        <>
          {/* Öppettider */}
          <section className="border-t pt-6">
            <h4 className="font-medium mb-3">{t("settings.clubVenue.sectionOpeningHours")}</h4>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>{t("settings.clubVenue.fieldOpens")}</Label>
                <Input type="time" value={hall.opening_time || "07:00"} onChange={(e) => updateHall("opening_time", e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>{t("settings.clubVenue.fieldCloses")}</Label>
                <Input type="time" value={hall.closing_time || "22:00"} onChange={(e) => updateHall("closing_time", e.target.value)} />
              </div>
            </div>
          </section>

          {/* Bokningsinställningar */}
          <section className="border-t pt-6">
            <h4 className="font-medium mb-3">{t("settings.clubVenue.sectionBookingSettings")}</h4>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>{t("settings.clubVenue.fieldMinDuration")}</Label>
                <NumberInput min={15} max={120} step={15} value={hall.min_duration_minutes ?? 30} onValueChange={(v) => updateHall("min_duration_minutes", v)} />
              </div>
              <div className="space-y-2">
                <Label>{t("settings.clubVenue.fieldMaxDuration")}</Label>
                <NumberInput min={30} max={300} step={15} value={hall.max_duration_minutes ?? 120} onValueChange={(v) => updateHall("max_duration_minutes", v)} />
              </div>
              <div className="space-y-2">
                <Label>{t("settings.clubVenue.fieldTimeStep")}</Label>
                <NumberInput min={5} max={60} step={5} value={hall.time_step_minutes ?? 15} onValueChange={(v) => updateHall("time_step_minutes", v)} />
                <p className="text-xs text-muted-foreground">{t("settings.clubVenue.timeStepHint")}</p>
              </div>
              <div className="space-y-2">
                <Label>{t("settings.clubVenue.fieldBookingHorizon")}</Label>
                <NumberInput min={1} max={90} value={hall.booking_horizon_days ?? 14} onValueChange={(v) => updateHall("booking_horizon_days", v)} />
                <p className="text-xs text-muted-foreground">{t("settings.clubVenue.bookingHorizonHint")}</p>
              </div>
            </div>
          </section>

          {/* Check-in */}
          <section className="border-t pt-6">
            <h4 className="font-medium mb-3">{t("settings.clubVenue.sectionCheckin")}</h4>
            <div className="flex items-center justify-between mb-4">
              <div>
                <Label>{t("settings.clubVenue.checkinEnabled")}</Label>
                <p className="text-xs text-muted-foreground">{t("settings.clubVenue.checkinEnabledDesc")}</p>
              </div>
              <Switch checked={(hall as any).checkin_enabled ?? false} onCheckedChange={(v) => updateHall("checkin_enabled", v)} />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>{t("settings.clubVenue.fieldCheckinOpens")}</Label>
                <NumberInput min={5} max={60} value={hall.checkin_opens_minutes ?? 15} onValueChange={(v) => updateHall("checkin_opens_minutes", v)} />
              </div>
              <div className="space-y-2">
                <Label>{t("settings.clubVenue.fieldCheckinCloses")}</Label>
                <NumberInput min={5} max={60} value={hall.checkin_closes_minutes ?? 15} onValueChange={(v) => updateHall("checkin_closes_minutes", v)} />
              </div>
            </div>
            <div className="mt-4">
              <h5 className="text-sm font-medium mb-2">{t("settings.clubVenue.locationVerification")}</h5>
              <div className="grid gap-4 sm:grid-cols-3">
                <div className="space-y-1">
                  <Label className="text-xs">{t("settings.clubVenue.fieldLatitude")}</Label>
                  <NumberInput step="0.000001" value={hall.latitude} onValueChange={(v) => updateHall("latitude", v)} placeholder="59.3053" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">{t("settings.clubVenue.fieldLongitude")}</Label>
                  <NumberInput step="0.000001" value={hall.longitude} onValueChange={(v) => updateHall("longitude", v)} placeholder="18.1285" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">{t("settings.clubVenue.fieldRadius")}</Label>
                  <NumberInput value={hall.checkin_radius_meters ?? 200} onValueChange={(v) => updateHall("checkin_radius_meters", v)} />
                </div>
              </div>
              <div className="flex items-center gap-3 mt-2">
                <Button type="button" variant="outline" size="sm" className="gap-1.5" onClick={handleGetPosition} disabled={fetchingLocation}>
                  {fetchingLocation ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <MapPin className="h-3.5 w-3.5" />}
                  {t("settings.clubVenue.btnGetPosition")}
                </Button>
              </div>
              <p className="text-[10px] text-muted-foreground mt-1.5">{t("settings.clubVenue.checkinRadiusHint")}</p>
            </div>
          </section>

          {/* Personalkoder */}
          <StaffAccessCodes orgId={(org as any).id} />

          {/* Väntelista */}
          <section className="border-t pt-6">
            <h4 className="font-medium mb-3">{t("settings.clubVenue.sectionWaitlist")}</h4>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>{t("settings.clubVenue.fieldOfferTime")}</Label>
                <NumberInput min={5} max={60} value={hall.waitlist_offer_minutes ?? 15} onValueChange={(v) => updateHall("waitlist_offer_minutes", v)} />
                <p className="text-xs text-muted-foreground">{t("settings.clubVenue.offerTimeHint")}</p>
              </div>
            </div>
          </section>
        </>
      )}
    </div>
  );
}
